/**
 * CALC boolean-expression wizard. Builds an AND/OR/NOT tree of field
 * comparisons visually, then hands the generated CALC string back to the step
 * via `onApply`. Reuses the codebook field list (via context) and emits the
 * engine's CALC operators.
 *
 * For arithmetic, ternary, or anything outside the boolean subset, the step's
 * raw expression box is the editor — the wizard refuses to import such a
 * string (shows a note) and starts fresh instead.
 */
import React, { useEffect, useState } from "react"
import { Modal, Alert } from "antd"
import {
  CalcGroup,
  CalcNode,
  emptyGroup,
  generateCalc,
  isCalcComplete,
  parseCalc,
} from "./calcExpression"
import CalcConditionGroup from "./CalcConditionGroup.component"

interface CalcWizardProps {
  open: boolean
  /** The step's current expression — pre-loaded for editing when parseable. */
  initialExpression: string
  onApply: (expression: string) => void
  onClose: () => void
}

const CalcWizard: React.FC<CalcWizardProps> = ({
  open,
  initialExpression,
  onApply,
  onClose,
}) => {
  const [ast, setAst] = useState<CalcGroup>(() => emptyGroup())
  const [importFailed, setImportFailed] = useState(false)

  // Re-seed the tree each time the wizard opens, from the step's current
  // expression. A non-empty expression the parser can't represent surfaces a
  // note and starts the user from an empty tree.
  useEffect(() => {
    if (!open) return
    const { ast: parsed, ok } = parseCalc(initialExpression)
    setAst(parsed)
    setImportFailed(!ok && initialExpression.trim() !== "")
  }, [open, initialExpression])

  const preview = generateCalc(ast)
  const canApply = ast.children.length > 0 && isCalcComplete(ast)

  return (
    <Modal
      title="Build a condition (CALC)"
      open={open}
      onCancel={onClose}
      onOk={() => {
        onApply(preview)
        onClose()
      }}
      okText="Use this expression"
      okButtonProps={{ disabled: !canApply }}
      width={680}
      destroyOnClose
    >
      {importFailed && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Couldn't import the current expression"
          description="The wizard only builds boolean conditions (AND/OR/NOT of comparisons). Your expression uses something else (e.g. arithmetic) — build a new condition here, or close and edit it in the raw expression box."
        />
      )}

      <div className="qc-calc-wizard__intro">
        This row is true when…
      </div>

      <CalcConditionGroup
        node={ast}
        onChange={(next: CalcNode) =>
          setAst(next.kind === "group" ? next : { kind: "group", op: "AND", children: [next] })
        }
      />

      <div className="qc-calc-wizard__preview">
        <span className="qc-calc-wizard__preview-label">Expression</span>
        <pre className="qc-mono qc-calc-wizard__preview-body">
          {preview || "# add a condition above"}
        </pre>
      </div>
    </Modal>
  )
}

export default CalcWizard
